import type { SearchGroup, SearchResult } from './search-types';

type DocValue = string | number | boolean | string[] | undefined;
type RawDocument = Record<string, DocValue>;

export interface RawHighlight {
  field?: string;
  snippet?: string;
  value?: string;
}

export interface RawHit {
  document: RawDocument;
  highlights?: RawHighlight[];
  highlight?: Record<string, { snippet?: string; value?: string }>;
}

function asString(value: DocValue): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asStringArray(value: DocValue): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function formatNorDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nb-NO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function snippetFromHighlight(hit: RawHit, field: string): string | undefined {
  const listed = hit.highlights?.find((h) => h.field === field);
  if (listed?.snippet || listed?.value) return listed.snippet ?? listed.value;
  const block = hit.highlight?.[field];
  return block?.snippet ?? block?.value;
}

function bestSnippet(hit: RawHit, preferredFields: string[]): string | undefined {
  for (const field of preferredFields) {
    const snippet = snippetFromHighlight(hit, field);
    if (snippet) return snippet;
  }
  const any = hit.highlights?.find((h) => h.snippet || h.value);
  return any?.snippet ?? any?.value;
}

function fallbackSnippet(text: string | undefined, max = 160): string | undefined {
  if (!text?.trim()) return undefined;
  const trimmed = text.trim().replace(/\s+/g, ' ');
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

export function mapEventHits(hits: RawHit[]): SearchResult[] {
  return hits.map((hit) => {
    const d = hit.document;
    const id = asString(d.id) ?? '';
    const caseId = asString(d.caseId);
    const title = asString(d.title) ?? id;
    const type = asString(d.type);
    const fromEmail = asString(d.fromEmail);
    const timestampIso = asString(d.timestampIso);
    const attachmentNames = asStringArray(d.attachmentNames);
    const hasAttachments = d.hasAttachments === true || attachmentNames.length > 0;
    const snippet =
      bestSnippet(hit, ['contentPreview', 'title', 'attachmentNames', 'fromEmail']) ??
      fallbackSnippet(asString(d.contentPreview));

    return {
      id,
      group: 'Hendelser' as SearchGroup,
      title,
      subtitle: timestampIso ? formatNorDate(timestampIso) : undefined,
      snippet,
      metaLabel: type,
      metaDetail: fromEmail,
      hasAttachments,
      attachmentNames: attachmentNames.length > 0 ? attachmentNames : undefined,
      href: caseId ? `/cases/${caseId}?event=${encodeURIComponent(id)}` : '/',
      caseId,
      eventId: id,
    };
  });
}

export function mapPeopleHits(hits: RawHit[]): SearchResult[] {
  return hits.map((hit) => {
    const d = hit.document;
    const id = asString(d.id) ?? '';
    const caseId = asString(d.caseId);
    const emails = asStringArray(d.emails);
    const snippet =
      bestSnippet(hit, ['navn', 'aliases', 'emails', 'phones', 'normalizedName']) ??
      fallbackSnippet(emails[0]);
    return {
      id,
      group: 'Personer' as SearchGroup,
      title: asString(d.navn) ?? id,
      snippet,
      metaDetail: emails[0],
      href: caseId ? `/cases/${caseId}` : '/',
      caseId,
    };
  });
}

export function mapDocumentHits(hits: RawHit[]): SearchResult[] {
  return hits.map((hit) => {
    const d = hit.document;
    const id = asString(d.id) ?? '';
    const caseId = asString(d.caseId);
    const filnavn = asString(d.filnavn) ?? id;
    const snippet =
      bestSnippet(hit, ['textContent', 'filnavn']) ?? fallbackSnippet(asString(d.textContent));
    return {
      id,
      group: 'Dokumenter' as SearchGroup,
      title: filnavn,
      snippet,
      metaLabel: asString(d.filtype),
      hasAttachments: true,
      attachmentNames: [filnavn],
      href: caseId ? `/cases/${caseId}` : '/',
      caseId,
    };
  });
}

export function mapOrgHits(hits: RawHit[]): SearchResult[] {
  return hits.map((hit) => {
    const d = hit.document;
    const id = asString(d.id) ?? '';
    const caseId = asString(d.caseId);
    const domains = asStringArray(d.domains);
    const snippet =
      bestSnippet(hit, ['navn', 'aliases', 'domains', 'orgNumber']) ??
      fallbackSnippet(domains[0] ?? asString(d.orgNumber));
    return {
      id,
      group: 'Organisasjoner' as SearchGroup,
      title: asString(d.navn) ?? id,
      snippet,
      metaDetail: domains[0] ?? asString(d.orgNumber),
      href: caseId ? `/cases/${caseId}` : '/',
      caseId,
    };
  });
}
